import { useNavigate, useSearchParams } from "react-router";

import { ChevronDown, EmptystateCodeFileColored } from "components/icons/generated";
import PageInfoHeader from "ds/components/PageInfoHeader";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import EmptyState from "ds/components/EmptyState";
import Button from "ds/components/Button";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Box from "ds/components/Box";
import useSpaceliftVcsAllFiles from "shared/SpaceliftVcsRepo/useSearchSpaceliftVcsFiles/useAllFiles";
import { Repo } from "types/generated";
import { getRepoUrl } from "utils/spaceliftRepo";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";

import { buildPathSearch } from "../helpers";
import SpaceliftVcsRepoFilesTable from "./Table";
import SpaceliftVcsRepoFilesTitle from "./Table/FilesTitle";
import useFilesNavigation from "./Table/useFilesNavigation";
import SpaceliftVcsRepoSearchResultsTable from "./Search";
import { SPACELIFT_VCS_REPO_SEARCH_FILTERS_STORAGE_KEY } from "./Search/constants";
import styles from "../styles.module.css";

type SpaceliftVcsRepoFilesProps = {
  repo: Pick<Repo, "id">;
};

const SpaceliftVcsRepoFiles = ({ repo }: SpaceliftVcsRepoFilesProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { folderPath } = useFilesNavigation();
  const { files, loading } = useSpaceliftVcsAllFiles({ repoId: repo.id });

  const repoUrl = getRepoUrl(repo.id);
  const searchQuery = getSearchQuery(searchParams);
  const hasNoFiles = !loading && files.length === 0;

  return (
    <>
      <PageInfoHeader
        title={
          searchQuery ? (
            <PageInfoHeaderTitle title="Search results" />
          ) : (
            <SpaceliftVcsRepoFilesTitle />
          )
        }
        className={styles.filesInfoHeader}
      >
        <Box direction="row" align="end" gap="medium">
          <SearchInput
            placeholder="Search all files..."
            filtersOrderSettingsKey={SPACELIFT_VCS_REPO_SEARCH_FILTERS_STORAGE_KEY}
            disabled={hasNoFiles}
          />
          <DropdownMenu
            triggerComponent={
              <Button variant="secondary" endIcon={ChevronDown}>
                Add file
              </Button>
            }
            placement="bottom right"
          >
            <DropdownMenuItem
              onAction={() => {
                navigate(`${repoUrl}/new${buildPathSearch(folderPath)}`);
              }}
            >
              Create file
            </DropdownMenuItem>
            <DropdownMenuItem
              onAction={() => {
                navigate(`${repoUrl}/upload${buildPathSearch(folderPath)}`);
              }}
            >
              Upload files
            </DropdownMenuItem>
          </DropdownMenu>
        </Box>
      </PageInfoHeader>

      {hasNoFiles && (
        <EmptyState
          title="No files yet"
          caption="Create or upload files to get started."
          icon={EmptystateCodeFileColored}
        />
      )}
      {!hasNoFiles && searchQuery && <SpaceliftVcsRepoSearchResultsTable />}
      {!hasNoFiles && !searchQuery && (
        <SpaceliftVcsRepoFilesTable files={files} loading={loading} />
      )}
    </>
  );
};

export default SpaceliftVcsRepoFiles;
